import { Tabs, Tab, Box } from '@material-ui/core';
import { useEffect, useState } from 'react';
import { ITabPanelProps } from '../../interface/tab-panel.interface';
import Participants from './Participants';
import Analytics from './Analytics';
import { ILearningGamePlayerRank } from '../../interface/learning-game-player-rank.interface';
import { Column, Workbook } from 'exceljs';
import * as fs from 'file-saver';
import {
  fetchJourney,
  getAnalytics,
  getGenieLearningGamePlayerLeaderboard,
} from '../../common/api/apicall';
import { months } from '../../common/constants';
import { useParams } from 'react-router-dom';
import { IDatableColumn } from '../../common/leaderboard/column.interface';

function isBeforeDate(isoDate: string) {
  const userDate = new Date(isoDate);
  // 20:00 IST = 14:30 UTC
  const targetDate = new Date(Date.UTC(2025, 10, 20, 14, 30, 0));

  // console.log({targetDate , userDate})
  return userDate < targetDate;
}

const formatTime = (seconds: number) => {
  const mins = Math.floor(seconds / 60)
    .toString()
    .padStart(2, '0');
  const secs = Math.floor(seconds % 60).toString().padStart(2, '0');
  return `${mins}:${secs}`;
};

function TabPanel(props: ITabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `full-width-tab-${index}`,
    'aria-controls': `full-width-tabpanel-${index}`,
  };
}

function getMaxScore(journey?: any) {
  if (!journey) {
    return undefined;
  }

  if (['SwipeGame', 'SwipeGame2' , 'Kbc'].includes(journey.gameFormat)) {
    return 100;
  }

  if (
    journey.gameFormat === 'JumblesGame' ||
    journey.gameFormat === 'CrosswordsGame' ||
    journey.gameFormat === 'WordsPuzzleGame'
  ) {
    return journey?.jumbles?.reduce((sum: number, item: any) => {
      const length = item.correctWord.replace(/\s+/g, '').length;
      return sum + length * 10;
    }, 0);
  }

  return (
    30 *
    (journey.questionSelectedCount
      ? journey.questionSelectedCount
      : journey.questions?.length || journey.jumbles?.length)
  );
}

function getSwipeGame2HighestScore(player: any) {
  const score = player['lastHighestScoreHistory'];

  if (score > 100) {
    return 100;
  }

  if (score < 100 && isBeforeDate(player['completedAt'])) {
    return Math.ceil(score / 10) * 10;
  }

  return score;
}

function getAbbSwipeGame2HighestScore(player: any) {
  if (!player['gameOver'] && isBeforeDate(player['completedAt'])) {
    return Math.round((player['lastHighestScoreHistory'] / 300) * 100);
  }

  return getSwipeGame2HighestScore(player);
}

const SBM_COMPANY_ID = '68c00e4b07fb52df2f1d42dc';
const ABB_COMPANY_ID = '68ee0b8c811929aacddc3d8d';
export default function GenieStaging(props: { env: 'prod' | 'staging' }) {
  const [value, setValue] = useState(0);
  const [data, setData] = useState<ILearningGamePlayerRank[]>([]);
  const [analyticsData, setAnalyticsData] = useState<ILearningGamePlayerRank[]>(
    [],
  );
  const { id: journeyId } = useParams();
  const [journeyDetails, setJourneyDetails] = useState<{
    name: string;
    divisionId: string;
    gameFormat: string;
    createdAt: string;
    companyId: string;
  }>();

  const newColumns =
    ((journeyDetails as any)?.gameFormat === 'JumblesGame' || (journeyDetails as any)?.gameFormat === 'CrosswordsGame' || (journeyDetails as any)?.gameFormat === 'WordsPuzzleGame'
      ? (journeyDetails as any)?.jumbles
      : (journeyDetails as any)?.questions
    )?.map((item: any, index: any) => {
      return {
        header: item.text || item.correctWord, //`Q${index + 1}`,
        key: `q-${item._id}`,
        width: 50,
        style: { alignment: { wrapText: true } },
      };
    }) || [];

  const maxScore = getMaxScore(journeyDetails);
  const highestScoreHeader = maxScore
    ? `Highest Score (out of ${maxScore})`
    : 'Highest Score';

  const worksheetColumns = [
    { header: 'Learner Id', key: 'learnerId', width: 40 } as Column,
    { header: 'Name', key: 'name', width: 40 } as Column,
    { header: 'Email', key: 'email', width: 40 } as Column,
    ...(journeyDetails?.companyId === '684a3ce1db4f66991573f720'
      ? [
          { header: 'Designation', key: 'designation', width: 30 } as Column,
          { header: 'Mobile Number', key: 'mobileNo', width: 30 } as Column,
          { header: 'CU/Zone', key: 'zone', width: 30 } as Column,
          { header: 'State', key: 'state', width: 30 } as Column,
          { header: 'Territory', key: 'territory', width: 30 } as Column,
          {
            header: 'Reporting Manager Name',
            key: 'manager',
            width: 40,
          } as Column,
        ]
      : []),
    {
      header: highestScoreHeader,
      key: 'highestScore',
      width: 30,
      alignment: { horizontal: 'center' },
    } as Column,
    {
      header: 'Score(percentage)',
      key: 'scorePercentage',
      width: 30,
      alignment: { horizontal: 'center' },
    } as Column,
    {
      header: 'attempts',
      key: 'attempts',
      width: 30,
      alignment: { horizontal: 'center' },
    } as Column,
    {
      header: 'Accuracy percentage (First attempt)',
      key: 'accuracy',
      width: 30,
    } as Column,
    {
      header: 'Overall Time (latest attempt)',
      key: 'elapsedTime',
      width: 30,
    } as Column,
    { header: 'Last Completed Date', key: 'completedAt', width: 30 } as Column,
    ...newColumns,
  ];

  useEffect(() => {
    async function getGenieJourney() {
      try {
        const journey = await fetchJourney(
          journeyId as string,
          props.env === 'prod',
        );
        let isJumbles = journey.gameFormat === 'JumblesGame' || journey.gameFormat === 'CrosswordsGame' || journey.gameFormat === 'WordsPuzzleGame'
        setJourneyDetails(journey);

        getAnalytics(journey.divisionId, props.env === 'prod', isJumbles).then(
          (analytics) => {
            console.log({ analytics });
            // Process the analytics data
            const processedData = analytics.map(
              (
                question: {
                  correctOnFirstAttempt: number;
                  totalPlayers: number;
                  questionText: any;
                  jumbleHint: any;
                  _id:string
                },
                index: number,
              ) => {
                const accuracy = (
                  (question.correctOnFirstAttempt / question.totalPlayers) *
                  100
                ).toFixed(2);
console.log(journey.jumbles.find((w)=>w._id === question._id)?.correctWord)
                return {
                  number: index + 1,
                  question: isJumbles
                    ? (journey.gameFormat === 'WordsPuzzleGame' ? journey.jumbles.find((w)=>w._id === question._id)?.correctWord : question.jumbleHint)
                    : question.questionText,
                  accuracy: question.totalPlayers ? `${accuracy}%` : '',
                };
              },
            );

            setAnalyticsData(processedData);
          },
        );

        getGenieLearningGamePlayerLeaderboard(
          journey.divisionId,
          props.env === 'prod',
          {
            includeNonPostive: true,
            scorePosition:
              journey.companyId === SBM_COMPANY_ID ? 'first' : undefined,
            companyId: journey.companyId,
          },
        )
          .then((res: any[]) => {
            const filteredPlayers: any[] = [];
            res.forEach((player, index) => {
              player['name'] =
                (player['firstName'] || '') +
                ' ' +
                (player['lastName'] || '') +
                (player['lastname'] || '');
              player['highestScore'] =
                journey.companyId === SBM_COMPANY_ID
                  ? player['highestScoreHistory']
                  : journey.companyId === ABB_COMPANY_ID &&
                    journey.gameFormat === 'SwipeGame2'
                  ? getAbbSwipeGame2HighestScore(player)
                  : journey.gameFormat === 'SwipeGame2'
                  ? getSwipeGame2HighestScore(player)
                  : player['lastHighestScoreHistory'];

              // (isJumbles ? journey?.jumbles : journey?.questions)?.forEach(
              //   (item: any) => {
              //     player[`q-${item._id}`] = 0;
              //   },
              // );
console.log({player})
              player.analytics?.[isJumbles ? 'jumbles' : 'questions']?.forEach(
                (item: any) => {
                  // 1s *100/ (0s +1s)
                  player[`q-${isJumbles ? item.jumbleId : item.questionId}`] =
                    item.attempt === 0 ? 1 : 0;
                },
              );

              const numberOfOnes = player?.['firstAttemptAnalytics']?.[
                isJumbles ? 'jumbles' : 'questions'
              ]?.filter((item: any) => item.attempt === 0)?.length;
              const numberOfOnesOrZeros =
                player?.['firstAttemptAnalytics']?.[isJumbles ? 'jumbles' : 'questions']?.length;
              // console.log({numberOfOnes , numberOfOnesOrZeros} , player.analytics.questions)

              const calculatedAccuracy =
                (numberOfOnes * 100) / numberOfOnesOrZeros;
              player['accuracy'] = isNaN(calculatedAccuracy)
                ? ''
                : calculatedAccuracy.toFixed(2);

              // console.log({numberOfOnesOrZeros , player})
              if (player.extraDetail) {
                player['employeeid'] = player.extraDetail['employeeid'];
                if (journey.companyId === ABB_COMPANY_ID) {
                  player['elapsedTime'] =
                    !isNaN(player.extraDetail['fullGameTimeRemaining']) &&
                    player.extraDetail['fullGameTimeRemaining'] < 720
                      ? formatTime(
                          720 - player.extraDetail['fullGameTimeRemaining'],
                        )
                      : '';
                } else {
                  player['elapsedTime'] = player.extraDetail['elapsedTime']
                    ? formatTime(player.extraDetail['elapsedTime'])
                    : '';
                }
              }

              if (player['email']?.trim()?.toLowerCase() === 'test@test.com') {
                player['email'] = '';
              }

              if (player['completedAt']) {
                const date = new Date(player['completedAt']);
                player['completedAt'] = !player['gameOver']
                  ? ``
                  : date.toLocaleDateString('en-US', {
                      day: 'numeric',
                      // weekday: 'short',
                      year: 'numeric',
                      month: 'long',
                      hour: 'numeric',
                      minute: 'numeric',
                      second: 'numeric',
                    });
                // player['completedAt'] = !player['gameOver']
                //   ? ``
                //   : `${date.getDate()} ${
                //       months[date.getMonth()]
                //     }, ${date.getFullYear()}`;
              }

              const maxScore = getMaxScore(journey);

              player['scorePercentage'] =
                Math.round(
                  ((player['highestScore'] - 0) * 100) / (maxScore + 0),
                ) || 0;
              console.log(player['highestScore'], player['email']);
              //     if (player.extraDetail) {
              filteredPlayers.push(player);
              //  }
            });
            setData(filteredPlayers);
          })
          .catch((error) => console.log('error: ', error));
      } catch (error) {}
    }
    getGenieJourney();
  }, []);

  function download() {
    const workbook = new Workbook();
    const participantSheet = workbook.addWorksheet('Participant data');
    participantSheet.columns = worksheetColumns;
    // worksheet.columns = worksheetColumns;
    participantSheet.addRows(data);

    const analyticsSheet = workbook.addWorksheet('Question level analytics');
    analyticsSheet.columns = [
      { header: 'Question no', key: 'number', width: 50 } as Column,
      { header: 'Question text', key: 'question', width: 50 } as Column,
      { header: 'Accuracy', key: 'accuracy', width: 50 } as Column,
    ];
    analyticsSheet.addRows(analyticsData);

    const legendSheet = workbook.addWorksheet('Legend');
    legendSheet.columns = [
      { header: 'Journey Name', key: 'name', width: 50 } as Column,
      { header: 'Creation Date', key: 'date', width: 50 } as Column,
      { header: 'Game Format', key: 'format', width: 50 } as Column,
    ];
    let date = new Date(journeyDetails?.createdAt as string);
    legendSheet.addRows([
      {
        name: journeyDetails?.name,
        format: journeyDetails?.gameFormat,
        date: `${date.getDate()} ${
          months[date.getMonth()]
        }, ${date.getFullYear()}`,
      },
    ]);

    workbook.xlsx.writeBuffer().then((data) => {
      const blob = new Blob([data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      fs.saveAs(
        blob,
        `${journeyDetails?.name}  (Participants data) ` + '.xlsx',
      );
    });
  }

  const handleChange = (event: any, newValue: number) => {
    console.log('event: ', event, 'newValue: ', newValue);

    setValue(newValue);
  };

  // const classes = useStyles();

  const visualPath =
    props.env === 'prod' ? 'showcase' : 'showcase-staging';

  const multiplayerFormats = new Set([
    'SliderGame',
    'MazeGame',
    'MarioGame',
    'AngryBirdGame',
    'BubbleShooterGame',
    'PathFinderGame',
    'ParadropGame',
    'SpotOnGame',
    'PoolGame',
    'SwipeGame2',
    'JumblesGame',
    'SwipeGame',
    'QuestionsGame',
  ]);

  const showMultiplayerBtn =
    props.env === 'staging' &&
    (journeyDetails as any)?.gameMode === 'MultiPlayer' &&
    !!journeyDetails?.gameFormat &&
    multiplayerFormats.has(journeyDetails.gameFormat);

  const [showMultiplayerModal, setShowMultiplayerModal] = useState(false);

  const multiplayerUrl = `https://staging.creator.talentlitmus.com/genie/multiplayer/${journeyId}`;

  const btnStyle: React.CSSProperties = {
    display: 'inline-block',
    width: 'fit-content',
    alignSelf: 'center',
    padding: '0.375rem 1rem',
    backgroundColor: '#1297c0',
    color: '#ffffff',
    borderRadius: '1.5rem',
    fontWeight: 600,
    fontSize: '0.875rem',
    textAlign: 'center',
    textDecoration: 'none',
    boxShadow: '2px 2px 0px 0px rgba(137,137,137,1)',
    cursor: 'pointer',
    border: 'none',
  };

  return (
    <>
      {showMultiplayerModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
          }}
          onClick={() => setShowMultiplayerModal(false)}
        >
          <div
            style={{
              backgroundColor: '#fff',
              borderRadius: '12px',
              padding: '28px 32px',
              maxWidth: '420px',
              width: '90%',
              boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <p style={{ margin: '0 0 10px', fontWeight: 700, fontSize: '1.3rem' }}>
              Do you want to resume an ongoing session or start a new session?
            </p>
            <p style={{ margin: '0 0 24px',  color: '#e05c2a' }}>
              If you start a new session, all the existing players' data will be deleted.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                style={{ ...btnStyle, backgroundColor: '#ff6150' }}
                onClick={() => {
                  setShowMultiplayerModal(false);
                  window.open(`${multiplayerUrl}?new=true`, '_blank');
                }}
              >
                Start New
              </button>
              <button
                style={btnStyle}
                onClick={() => {
                  setShowMultiplayerModal(false);
                  window.open(multiplayerUrl, '_blank');
                }}
              >
                Resume
              </button>
            </div>
          </div>
        </div>
      )}

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: '8px',
          padding: '12px 16px 0',
        }}
      >
        <a
          href={`/${visualPath}/${journeyId}`}
          target="_blank"
          rel="noopener noreferrer"
          style={btnStyle}
        >
          Leaderboard Showcase
        </a>

        {showMultiplayerBtn && (
          <button
            style={btnStyle}
            onClick={() => {
              if ((journeyDetails as any)?.multiplayerSid) {
                setShowMultiplayerModal(true);
              } else {
                window.open(multiplayerUrl, '_blank');
              }
            }}
          >
            Multiplayer Session
          </button>
        )}
      </div>
      <Tabs
        value={value}
        variant="scrollable"
        scrollButtons="auto"
        onChange={(event, i) => handleChange(event, i)}
        aria-label="scrollable auto tabs example"
      >
        <Tab label="Participants" style={{ fontSize: 13 }} {...a11yProps(0)} />
        <Tab label="Analytics" style={{ fontSize: 13 }} {...a11yProps(1)} />
      </Tabs>
      <TabPanel value={value} index={0}>
        <Participants
          participantsData={data}
          analyticsData={analyticsData}
          download={download}
          journeyDetails={journeyDetails!}
          maxScore={maxScore}
        />
      </TabPanel>
      <TabPanel value={value} index={1}>
        <Analytics
          participantsData={data}
          analyticsData={analyticsData}
          download={download}
          journeyDetails={journeyDetails!}
        />
      </TabPanel>
    </>
  );
}
